import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ChevronRight } from '@hqo/react-components-library/dist/icons';

import { BookingStatus } from 'store/bookings/types';
import { TransactionDetails } from 'store/transactions/types';
import { bookingWhen } from 'utils/bookingWhen';
import { getStatusPillLabelId, getStatusPillVariant } from 'components/booking-tile/redesigned/status-pill.util';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { useLocale } from 'hooks/use-locale.hook';

import {
  RowChevron,
  RowName,
  RowSubtitle,
  RowText,
  RowThumb,
  RowThumbImage,
  StyledResourceRow,
} from 'components/resources-list-item/styles';
import { StatusDot, StatusLabel, StatusPillContainer } from 'components/booking-tile/redesigned/styles';
import { BookingSummaryCard, BookingSummaryMeta, BookingSummaryWhen, ScreenReaderOnly } from './styles';

/* Suffixed with the booking id: two tiles on one page must not share a description id. */
const BOOKING_SUMMARY_HINT_ID = 'booking-summary-hint';

interface BookingSummaryTileProps {
  booking: TransactionDetails;
  onClick: VoidFunction;
}

/*
 * The booking as one tappable row, composed from the resource list's row card and the my-bookings
 * status pill — both treatments a tenant has already seen this booking in.
 *
 * Not `RedesignedBookingTile` (check-in, cancel confirmation, message badge, temperature vote) and not
 * `ResourceItem` (keyed on a resource; wants capacity and price, offers no status).
 */
export const BookingSummaryTile = ({ booking, onClick }: BookingSummaryTileProps): JSX.Element => {
  const intl = useIntl();
  const locale = useLocale();
  const timeZone = useSelector(selectBuildingTimeZone);

  const details = booking?.details;
  const status = details?.status as BookingStatus | undefined;
  /*
   * Not `formatMessage({ id: getStatusPillLabelId(status) })`: that id is `undefined` for a status
   * with no tenant-facing word, and formatMessage throws on an undefined id rather than falling back.
   * Resolving first makes "is there a word for this?" the same question as "is there a pill?".
   */
  const statusLabelId = getStatusPillLabelId(status);

  const when = bookingWhen({
    start: details?.start_at,
    end: details?.end_at,
    locale: locale as string,
    timeZone,
  });

  /* A div with role=button, matching the row this extends, so the keyboard activation is explicit. */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  /* `content.resource.image_url` is not always populated; the thumb keeps its grey and the row its rhythm. */
  const imageUrl = booking?.content?.resource?.image_url;

  /*
   * A description, not an aria-label: the button's name is its own contents, and replacing it would
   * throw away the status and the time.
   */
  const describedBy = `${BOOKING_SUMMARY_HINT_ID}-${details?.id ?? 'booking'}`;

  return (
    <BookingSummaryCard data-testid="booking-summary-tile">
      <ScreenReaderOnly id={describedBy}>{intl.formatMessage({ id: 'booking_details' })}</ScreenReaderOnly>
      <StyledResourceRow
        role="button"
        tabIndex={0}
        aria-describedby={describedBy}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        <RowThumb>{imageUrl && <RowThumbImage src={imageUrl} alt="" />}</RowThumb>
        <RowText>
          <RowName data-testid="booking-summary-tile-name">{details?.name}</RowName>
          {details?.location_name && <RowSubtitle>{details.location_name}</RowSubtitle>}
          <BookingSummaryMeta>
            {!!statusLabelId && (
              <StatusPillContainer variant={getStatusPillVariant(status)} data-testid="booking-summary-tile-status">
                <StatusDot variant={getStatusPillVariant(status)} />
                <StatusLabel variant={getStatusPillVariant(status)}>
                  {intl.formatMessage({ id: statusLabelId })}
                </StatusLabel>
              </StatusPillContainer>
            )}
            {when && <BookingSummaryWhen>{when}</BookingSummaryWhen>}
          </BookingSummaryMeta>
        </RowText>
        <RowChevron aria-hidden="true">
          <ChevronRight size="sm" variant="adminGreyDark" />
        </RowChevron>
      </StyledResourceRow>
    </BookingSummaryCard>
  );
};
